import type {Well} from '@/types/well';

// Multi-well selection for pages that let the user pick a single well, a
// whole lease, or an arbitrary set of wells — first consumer is
// ProductionReportPage.tsx (client request, 2026-09-22: "make the
// Production Report take multiple wells... almost always when they want
// multiple wells, it will be all wells in a lease"). Design confirmed
// with Duane the same day via a design-review artifact: lease membership
// is always resolved LIVE (a well added to or removed from a lease later
// is picked up automatically, never a point-in-time snapshot), and this
// picker should support arbitrary multi-well picks too, not just whole
// leases, from the start.
//
// leaseIds/wellIds are kept disjoint by WellLeaseSelect (selecting every
// well of a lease individually does NOT get folded into leaseIds — only
// its own "select this whole lease" control does that; see that
// component's own comment) — resolveWellSelection below dedupes
// defensively regardless, so a selection built by hand (e.g. a future
// deep-link) doesn't need to maintain that invariant itself.
export interface WellSelection {
   leaseIds: string[];
   wellIds: string[];
}

export const EMPTY_WELL_SELECTION: WellSelection = {leaseIds: [], wellIds: []};

export function isEmptyWellSelection(selection: WellSelection): boolean {
   return selection.leaseIds.length === 0 && selection.wellIds.length === 0;
}

/**
 * Live-resolves a WellSelection against the current wells list — a
 * selected lease always expands to whichever wells carry that leaseId
 * right now (Duane's "always live" call, 2026-09-22), not whatever the
 * lease's membership was at selection time. Sorted by lease then well
 * name, same convention as WellAutocomplete/ProductionReportPage's own
 * former sortedWells.
 */
export function resolveWellSelection(selection: WellSelection, wells: Well[]): Well[] {
   if (isEmptyWellSelection(selection)) return [];
   const leaseIdSet = new Set(selection.leaseIds);
   const wellIdSet = new Set(selection.wellIds);
   return wells
      .filter((w) => (w.leaseId != null && leaseIdSet.has(w.leaseId)) || wellIdSet.has(w.id))
      .sort((a, b) => a.leaseName.localeCompare(b.leaseName) || a.wellName.localeCompare(b.wellName));
}

export function isWellInSelection(well: Well, selection: WellSelection): boolean {
   return (well.leaseId != null && selection.leaseIds.includes(well.leaseId)) || selection.wellIds.includes(well.id);
}

/**
 * Toggles one well. If the well is currently covered by a whole-lease
 * selection, this SPLITS that selection — the lease is dropped from
 * leaseIds and every one of its other wells is added to wellIds
 * individually, so unchecking one well out of an otherwise-fully-selected
 * lease keeps the rest selected instead of clearing the whole lease.
 * `wellsInSameLease` should be every well sharing `well.leaseId` (ignored
 * when `well.leaseId` is null — a well with no lease can never be
 * lease-covered, so it always just toggles in/out of wellIds directly).
 */
export function toggleWell(well: Well, wellsInSameLease: Well[], selection: WellSelection): WellSelection {
   const isLeaseCovered = well.leaseId != null && selection.leaseIds.includes(well.leaseId);
   if (isLeaseCovered) {
      const otherWellIds = wellsInSameLease.filter((w) => w.id !== well.id).map((w) => w.id);
      return {
         leaseIds: selection.leaseIds.filter((id) => id !== well.leaseId),
         wellIds: [...new Set([...selection.wellIds, ...otherWellIds])],
      };
   }
   const alreadyIndividual = selection.wellIds.includes(well.id);
   return {
      leaseIds: selection.leaseIds,
      wellIds: alreadyIndividual ? selection.wellIds.filter((id) => id !== well.id) : [...selection.wellIds, well.id],
   };
}

/**
 * Toggles an entire lease group (the group-header checkbox in
 * WellLeaseSelect). `leaseId` is null for the "no lease" bucket, which
 * can never become a leaseIds entry — it just toggles every one of its
 * wells into/out of wellIds together. Otherwise: selecting adds leaseId
 * to leaseIds and removes any of its wells' ids from wellIds (now
 * redundant — they're covered by the lease); deselecting removes leaseId
 * and drops every one of its wells from wellIds too.
 */
export function toggleLease(leaseId: string | null, leaseWells: Well[], selection: WellSelection): WellSelection {
   const leaseWellIds = leaseWells.map((w) => w.id);
   const allSelected = leaseWells.length > 0 && leaseWells.every((w) => isWellInSelection(w, selection));

   if (leaseId == null) {
      return {
         leaseIds: selection.leaseIds,
         wellIds: allSelected
            ? selection.wellIds.filter((id) => !leaseWellIds.includes(id))
            : [...new Set([...selection.wellIds, ...leaseWellIds])],
      };
   }

   if (allSelected) {
      return {
         leaseIds: selection.leaseIds.filter((id) => id !== leaseId),
         wellIds: selection.wellIds.filter((id) => !leaseWellIds.includes(id)),
      };
   }
   return {
      leaseIds: [...new Set([...selection.leaseIds, leaseId])],
      wellIds: selection.wellIds.filter((id) => !leaseWellIds.includes(id)),
   };
}
